/**
 * @fileoverview Pivot report builder (MOR-906 §7.8)
 *
 * The configurable general case of the REM pack: choose what runs down the
 * rows, what runs across the columns, which measures fill the cells, and
 * which entries are included. That composition happens in the field panel
 * (PivotFieldPanel), the way a spreadsheet does it; this file owns the
 * period, the scope, the totals options and saving. The result is the table
 * remuneration reports actually contain, and it exports to Excel exactly as
 * rendered.
 *
 * Reports save per user and re-run live, so a saved table is never stale.
 * The starter set (Remuneration table, WIP by matter and category, Hours by
 * person and week) is seeded as ordinary editable rows — the REM pack tab
 * stays as the fixed one-click version.
 *
 * @component
 * @example
 * <PivotReportBuilder orgId={orgId} pendingSpec={spec} onPendingConsumed={clear} />
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format } from "date-fns";
import {
  BookmarkPlus,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Save,
  Star,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimePivotSpec } from "@cvx/timesheets/savedPivotReports/schema";
import { validateTokens } from "@cvx/timesheets/query/entryQuery";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { formatDateRange } from "./format";
import { PivotFieldPanel } from "./PivotFieldPanel";
import { PivotTable } from "./PivotTable";
import {
  DIMENSION_LABELS,
  exportPivotToExcel,
  MEASURE_LABELS,
  PERIOD_LABELS,
} from "./pivotExport";
import { useEntryQueryDimensions } from "./useEntryQueryDimensions";

// The usual card shadow reads against the muted page, but the builder opens
// on the dialog's white surface, where it disappears — hence the border.
const CARD_CLASS =
  "rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

// Order is a builder concern; the wording is shared with the report library.
const PRESETS = (
  [
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "this_quarter",
    "last_30",
    "last_90",
    "custom",
  ] as const
).map((key) => ({ key, label: PERIOD_LABELS[key] }));

/** A blank table that already reads like something useful. */
const DEFAULT_PIVOT_SPEC: TimePivotSpec = {
  scope: "me",
  preset: "this_month",
  rows: ["job"],
  columns: ["category"],
  measures: ["hours", "amount"],
  showGst: false,
  showSubtotals: false,
};

export function PivotReportBuilder({
  orgId,
  pendingSpec,
  pendingTitle,
  onPendingConsumed,
  pinOnSave,
}: {
  orgId: Id<"organizations">;
  /** A spec handed over from the AI ask box, adopted once then cleared. */
  pendingSpec?: TimePivotSpec | null;
  pendingTitle?: string | null;
  onPendingConsumed?: () => void;
  /** Opened from the board, so Save is also the gesture that pins it there. */
  pinOnSave?: boolean;
}) {
  const [spec, setSpec] = useState<TimePivotSpec>(DEFAULT_PIVOT_SPEC);
  const [fieldsOpen, setFieldsOpen] = useState(true);
  const [title, setTitle] = useState("Untitled table");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const today = format(new Date(), "yyyy-MM-dd");

  const patch = (p: Partial<TimePivotSpec>) =>
    setSpec((prev) => ({ ...prev, ...p }));

  const { context, dimensions } = useEntryQueryDimensions(orgId, spec.scope);

  // An AI answer is a spec like any other — adopt it so it stays editable.
  useEffect(() => {
    if (!pendingSpec) return;
    setSpec(pendingSpec);
    if (pendingTitle) {
      setTitle(pendingTitle);
      setSaveName(pendingTitle);
    }
    onPendingConsumed?.();
  }, [pendingSpec, pendingTitle, onPendingConsumed]);

  // A half-built expression is a normal state mid-edit, not a failure — the
  // builder shows the reason inline, so hold the run rather than erroring.
  const filterError = validateTokens(spec.filter ?? []);

  const { data: result, error } = useQuery({
    ...convexQuery(api.timesheets.pivot.runPivot, { orgId, spec, today }),
    enabled: !!context && !filterError,
  });

  const { data: saved } = useQuery(
    convexQuery(api.timesheets.pivotReports.listSaved, { orgId }),
  );
  const saveReport = useMutation(api.timesheets.pivotReports.saveReport);
  const deleteReport = useMutation(api.timesheets.pivotReports.deleteReport);
  const setFavourite = useMutation(api.timesheets.pivotReports.setFavourite);

  const canOrgScope = context?.canReportFirmWide ?? false;

  // Anyone who can see firm-wide time usually wants it, but whether they can
  // is only known once the context lands. Adjusted during render, not in an
  // effect: the context is normally already cached when the builder mounts,
  // so an effect would paint "My time" selected for a frame and visibly flip.
  // Identity against the default keeps this off an adopted or loaded spec.
  const [scopeDefaulted, setScopeDefaulted] = useState(false);
  if (canOrgScope && !scopeDefaulted) {
    setScopeDefaulted(true);
    setSpec((prev) =>
      prev === DEFAULT_PIVOT_SPEC ? { ...prev, scope: "org" } : prev,
    );
  }
  // The reverse case (MOR-1017): a saved Firm-scope report opened by someone
  // who cannot report firm-wide (a manager limited to their team). Rather
  // than refuse with a remedy the hidden scope control cannot perform, show
  // their own time and say so.
  const [narrowedFromFirm, setNarrowedFromFirm] = useState(false);
  if (context !== undefined && !canOrgScope && spec.scope === "org") {
    setNarrowedFromFirm(true);
    setSpec((prev) => ({ ...prev, scope: "me" }));
  }

  const rowInner = spec.rows[1];
  const outerLabel = spec.rows[0] ? DIMENSION_LABELS[spec.rows[0]] : "row";

  /** The shape of the table, for the collapsed header to stand in for. */
  const summary = [
    spec.rows.map((r) => DIMENSION_LABELS[r]).join(" › ") +
      (spec.columns[0] ? ` × ${DIMENSION_LABELS[spec.columns[0]]}` : ""),
    spec.measures.map((m) => MEASURE_LABELS[m]).join(", "),
    spec.preset === "custom" && spec.startDate && spec.endDate
      ? formatDateRange(spec.startDate, spec.endDate)
      : (PERIOD_LABELS[spec.preset] ?? spec.preset),
    canOrgScope ? (spec.scope === "org" ? "Firm" : "My time") : null,
    (spec.filter ?? []).length > 0 ? "filtered" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleSave = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      const name = saveName.trim();
      const reportId = await saveReport({ orgId, name, spec });
      if (pinOnSave) await setFavourite({ reportId, isFavourite: true });
      setTitle(name);
      toast.success(
        pinOnSave ? `Saved "${name}" to your dashboard.` : `Saved "${name}".`,
      );
      setSaveOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the report."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {(saved ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Saved tables:
          </span>
          {(saved ?? []).map((r) => (
            <span
              key={r._id}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background pl-2.5 text-xs"
            >
              <button
                type="button"
                className="py-1 hover:text-primary"
                onClick={() => {
                  setSpec(r.spec);
                  setTitle(r.name);
                  // Pre-filling the name makes Save an update in place.
                  setSaveName(r.name);
                }}
                title="Load this report — Save updates it in place"
              >
                {r.name}
              </button>
              <button
                type="button"
                className={cn(
                  "rounded-full p-1",
                  r.isFavourite
                    ? "text-amber-500"
                    : "text-muted-foreground hover:text-amber-500",
                )}
                onClick={() =>
                  setFavourite({
                    reportId: r._id,
                    isFavourite: !r.isFavourite,
                  }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not star the table."),
                    ),
                  )
                }
                aria-label={
                  r.isFavourite
                    ? `Remove ${r.name} from the Dashboard`
                    : `Pin ${r.name} to the Dashboard`
                }
                title={
                  r.isFavourite
                    ? "Remove from the Dashboard board"
                    : "Pin to the Dashboard board"
                }
              >
                <Star
                  className={cn("size-3", r.isFavourite && "fill-current")}
                />
              </button>
              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteReport({ reportId: r._id }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not delete the report."),
                    ),
                  )
                }
                aria-label={`Delete ${r.name}`}
              >
                <Trash2 className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Layout — collapsible, because a finished table is long and the
          fields that built it stop being interesting once it is right. */}
      <div className={cn(CARD_CLASS, "flex flex-col gap-3 p-4")}>
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 gap-1 px-2"
            onClick={() => setFieldsOpen((prev) => !prev)}
            aria-expanded={fieldsOpen}
          >
            {fieldsOpen ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
            Fields
          </Button>
          {!fieldsOpen && (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {summary}
            </span>
          )}
        </div>

        {fieldsOpen && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Period</Label>
                <Select
                  value={spec.preset}
                  onValueChange={(value) => patch({ preset: value })}
                >
                  <SelectTrigger className="h-8 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRESETS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {spec.preset === "custom" && (
                <>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">From</Label>
                    <DatePicker
                      value={spec.startDate ?? ""}
                      onChange={(isoDate) =>
                        patch({ startDate: isoDate ?? undefined })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">To</Label>
                    <DatePicker
                      value={spec.endDate ?? ""}
                      onChange={(isoDate) =>
                        patch({ endDate: isoDate ?? undefined })
                      }
                    />
                  </div>
                </>
              )}
              {narrowedFromFirm && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  This report was saved on Firm scope. Firm-wide reports are for
                  approvers of everyone, so it is showing your own time.
                </p>
              )}
              {canOrgScope && (
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Scope</Label>
                  <div className="flex items-center rounded-md border border-border">
                    <Button
                      variant={spec.scope === "org" ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 rounded-r-none"
                      onClick={() => patch({ scope: "org" })}
                    >
                      Firm
                    </Button>
                    <Button
                      variant={spec.scope === "me" ? "secondary" : "ghost"}
                      size="sm"
                      className="h-8 rounded-l-none"
                      onClick={() => patch({ scope: "me" })}
                    >
                      My time
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-border/50 pt-3">
              <PivotFieldPanel
                spec={spec}
                onChange={patch}
                dimensions={dimensions}
              />
            </div>

            {/* Only offered where they mean something: GST needs a value to tax,
            subtotals need an inner row level to sum. */}
            {(spec.measures.includes("amount") || rowInner !== undefined) && (
              <div className="flex flex-wrap items-center gap-3 border-t border-border/50 pt-3">
                {spec.measures.includes("amount") && (
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox
                      checked={spec.showGst}
                      onCheckedChange={(checked) =>
                        patch({ showGst: checked === true })
                      }
                    />
                    Add GST line
                  </label>
                )}
                {rowInner !== undefined && (
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox
                      checked={spec.showSubtotals}
                      onCheckedChange={(checked) =>
                        patch({ showSubtotals: checked === true })
                      }
                    />
                    Subtotal each {outerLabel.toLowerCase()}
                  </label>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Result — live and reactive. Exporting and saving act on this table,
          so they live on it, and on a header that survives each re-run
          rather than blinking out with the result. */}
      <div className={cn(CARD_CLASS, "flex min-w-0 flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold">{title}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!result || result.rows.length === 0}
              onClick={() => result && exportPivotToExcel(result, title)}
            >
              <Download className="size-4" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!!filterError}
              onClick={() => setSaveOpen(true)}
            >
              <BookmarkPlus className="size-4" />
              Save
            </Button>
          </div>
        </div>
        {filterError ? (
          <p className="py-6 text-sm text-muted-foreground">
            Finish the filter above to run this report.
          </p>
        ) : error ? (
          <p className="py-6 text-sm text-destructive">
            {extractErrorMessage(error, "Could not run the report.")}
          </p>
        ) : result === undefined ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : (
          <PivotTable
            result={result}
            title={title}
            hideTitle
            spec={spec}
            onSpecChange={patch}
          />
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this report</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="pivot-name">Name</Label>
            <Input
              id="pivot-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "Remuneration table — March"'
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
              }}
            />
            <p className="text-xs text-muted-foreground">
              Saved reports re-run live every time you open them. Saving with an
              existing name updates that report.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!saveName.trim() || saving}
              onClick={() => void handleSave()}
            >
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              Save report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
